import { useEffect } from 'react';
import { App, Form, Input, Modal, Select } from 'antd';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useCreateProfile, useUpdateProfile } from '@/hooks/useProfiles';
import type { ProfileDto } from '@/types/profile';

interface Props {
  open: boolean;
  profile: ProfileDto | null;
  onClose: () => void;
}

interface FormValues {
  enterpriseId: number;
  name: string;
  displayNameEs: string;
  description?: string;
}

export function ProfileFormModal({ open, profile, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const isEdit = profile != null;

  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const createMut = useCreateProfile();
  const updateMut = useUpdateProfile();

  useEffect(() => {
    if (open) {
      if (profile) {
        form.setFieldsValue({
          enterpriseId: profile.enterpriseId,
          name: profile.name,
          displayNameEs: profile.displayNameEs,
          description: profile.description ?? '',
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, profile, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (isEdit && profile) {
        await updateMut.mutateAsync({
          id: profile.id,
          request: {
            displayNameEs: values.displayNameEs.trim(),
            description: values.description?.trim() || undefined,
          },
        });
        message.success('Perfil actualizado.');
      } else {
        await createMut.mutateAsync({
          enterpriseId: values.enterpriseId,
          name: values.name.trim(),
          displayNameEs: values.displayNameEs.trim(),
          description: values.description?.trim() || undefined,
        });
        message.success('Perfil creado.');
      }

      onClose();
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  return (
    <Modal
      title={isEdit ? `Editar perfil: ${profile?.displayNameEs}` : 'Nuevo perfil'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? 'Guardar cambios' : 'Crear'}
      cancelText="Cancelar"
      confirmLoading={createMut.isPending || updateMut.isPending}
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Enterprise"
          name="enterpriseId"
          rules={[{ required: true, message: 'Enterprise requerida.' }]}
        >
          <Select
            placeholder="Selecciona una Enterprise"
            options={(enterprises?.items ?? []).map((e) => ({
              value: e.id,
              label: e.displayNameEs,
            }))}
            disabled={isEdit}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          label="Nombre tecnico"
          name="name"
          rules={[
            { required: true, message: 'Nombre requerido.' },
            { pattern: /^[A-Z][A-Z0-9_-]*$/i, message: 'Solo letras, numeros, guion y guion bajo.' },
          ]}
          extra="Identificador unico dentro de la Enterprise (ej: INSTALACIONES, CLARO-VENTAS)"
        >
          <Input
            placeholder="INSTALACIONES"
            disabled={isEdit}
            style={{ textTransform: 'uppercase' }}
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          label="Nombre para mostrar"
          name="displayNameEs"
          rules={[{ required: true, message: 'Nombre para mostrar requerido.' }]}
        >
          <Input placeholder="Tecnicos de Instalacion" maxLength={100} />
        </Form.Item>

        <Form.Item label="Descripcion" name="description">
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
